import React from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  Box,
  Flex,
  Heading,
  Text,
  Card,
  CardHeader,
  CardContent,
  Button,
  TagBadge,
  DataTable,
  LoadingOrbit,
  Select,
  Divider,
} from '@chatsdet/ui';
import { Gear, Plus, Star, Trash, PencilSimple } from '@phosphor-icons/react';
import type { ColumnDef, StockFeatures } from '@tanstack/react-table';
import {
  useBenchmarkConfigsQuery,
  useCreateBenchmarkConfigMutation,
  useUpdateBenchmarkConfigMutation,
  useDeleteBenchmarkConfigMutation,
  useSetDefaultConfigMutation,
  type BenchmarkConfig,
  type BenchmarkJobType,
  jobTypeLabels,
  formatDateTime,
} from './useBenchmarkData';
import { useProviderModelOptions, modelOptionsForProvider } from './useAiModelCatalog';

const JOB_TYPE_OPTIONS = [{ id: 'workflow_suite', label: 'Workflow Suite' }];

type ConfigFormData = {
  name: string;
  description: string;
  jobType: BenchmarkJobType;
  provider: string;
  model: string;
  promptVersion: string;
  jobConfig: string;
  isDefault: boolean;
};

const initialFormData: ConfigFormData = {
  name: '',
  description: '',
  jobType: 'workflow_suite',
  provider: '',
  model: '',
  promptVersion: '',
  jobConfig: '{}',
  isDefault: false,
};

export function SuperadminBenchmarkConfigs() {
  const _navigate = useNavigate();
  const [filterJobType, setFilterJobType] = React.useState<string>('all');
  const [editingConfig, setEditingConfig] = React.useState<BenchmarkConfig | null>(null);
  const [showForm, setShowForm] = React.useState(false);
  const [formData, setFormData] = React.useState<ConfigFormData>(initialFormData);
  const [formError, setFormError] = React.useState<string | null>(null);

  const { catalog, catalogQuery, providerOptions, modelOptions } = useProviderModelOptions(
    formData.provider,
    { providerDefault: 'Default', modelDefault: '— none —' }
  );

  const configsQuery = useBenchmarkConfigsQuery(
    filterJobType !== 'all' ? { jobType: filterJobType as BenchmarkJobType } : undefined
  );
  const createMutation = useCreateBenchmarkConfigMutation();
  const updateMutation = useUpdateBenchmarkConfigMutation();
  const deleteMutation = useDeleteBenchmarkConfigMutation();
  const setDefaultMutation = useSetDefaultConfigMutation();

  const handleNewConfig = () => {
    setEditingConfig(null);
    setFormData(initialFormData);
    setFormError(null);
    setShowForm(true);
  };

  const handleEditConfig = (config: BenchmarkConfig) => {
    setEditingConfig(config);
    setFormData({
      name: config.name,
      description: config.description ?? '',
      jobType: config.jobType,
      provider: config.provider ?? '',
      model: config.model ?? '',
      promptVersion: config.promptVersion ?? '',
      jobConfig: config.jobConfig ? JSON.stringify(config.jobConfig, null, 2) : '{}',
      isDefault: config.isDefault,
    });
    setFormError(null);
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingConfig(null);
    setFormData(initialFormData);
    setFormError(null);
  };

  const handleSubmit = () => {
    setFormError(null);

    if (!formData.name.trim()) {
      setFormError('Name is required');
      return;
    }

    let parsedJobConfig: Record<string, unknown> | undefined;
    if (formData.jobConfig.trim()) {
      try {
        parsedJobConfig = JSON.parse(formData.jobConfig);
      } catch {
        setFormError('Job config must be valid JSON');
        return;
      }
    }

    const payload = {
      name: formData.name.trim(),
      description: formData.description.trim() || undefined,
      jobType: formData.jobType,
      provider: formData.provider || undefined,
      model: formData.model.trim() || undefined,
      promptVersion: formData.promptVersion.trim() || undefined,
      jobConfig: parsedJobConfig,
      isDefault: formData.isDefault,
    };

    if (editingConfig) {
      updateMutation.mutate(
        { id: editingConfig.id, ...payload },
        {
          onSuccess: () => handleCloseForm(),
          onError: (error) => setFormError(error.message),
        }
      );
    } else {
      createMutation.mutate(payload, {
        onSuccess: () => handleCloseForm(),
        onError: (error) => setFormError(error.message),
      });
    }
  };

  const handleDelete = (config: BenchmarkConfig) => {
    if (!confirm(`Delete config "${config.name}"? This cannot be undone.`)) return;
    deleteMutation.mutate(config.id);
  };

  const handleSetDefault = (config: BenchmarkConfig) => {
    setDefaultMutation.mutate(config.id);
  };

  const columns: ColumnDef<StockFeatures, BenchmarkConfig>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => (
        <Flex align="center" gap="sm">
          <Text weight="medium">{row.original.name}</Text>
          {row.original.isDefault && (
            <TagBadge
              name="Default"
              color="yellow"
              size="sm"
              icon={<Star size={12} weight="fill" />}
            />
          )}
        </Flex>
      ),
    },
    {
      accessorKey: 'jobType',
      header: 'Job Type',
      cell: ({ row }) => (
        <TagBadge name={jobTypeLabels[row.original.jobType]} color="gray" hideDot />
      ),
    },
    {
      accessorKey: 'provider',
      header: 'Provider',
      cell: ({ row }) => <Text>{row.original.provider ?? 'Default'}</Text>,
    },
    {
      accessorKey: 'model',
      header: 'Model',
      cell: ({ row }) => (
        <Text className="truncate max-w-[150px]">{row.original.model ?? '—'}</Text>
      ),
    },
    {
      accessorKey: 'version',
      header: 'Version',
      cell: ({ row }) => <Text className="text-muted-foreground">v{row.original.version}</Text>,
    },
    {
      accessorKey: 'updatedAt',
      header: 'Updated',
      cell: ({ row }) => (
        <Text size="sm" className="text-muted-foreground">
          {formatDateTime(row.original.updatedAt)}
        </Text>
      ),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <Flex gap="sm" justify="end">
          {!row.original.isDefault && (
            <Button
              variant="ghost"
              size="sm"
              onPress={() => handleSetDefault(row.original)}
              isDisabled={setDefaultMutation.isPending}
              aria-label="Set as default"
            >
              <Star size={16} />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onPress={() => handleEditConfig(row.original)}
            aria-label="Edit"
          >
            <PencilSimple size={16} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onPress={() => handleDelete(row.original)}
            isDisabled={deleteMutation.isPending}
            aria-label="Delete"
            className="text-destructive hover:text-destructive"
          >
            <Trash size={16} />
          </Button>
        </Flex>
      ),
    },
  ];

  const jobTypeFilterOptions = [{ id: 'all', label: 'All job types' }, ...JOB_TYPE_OPTIONS];

  return (
    <Box className="space-y-6">
      <Flex justify="between" align="center" wrap gap="md">
        <div>
          <Heading level={2} className="flex items-center gap-2">
            <Gear size={24} className="text-primary" />
            Benchmark Configurations
          </Heading>
          <Text className="text-muted-foreground mt-1">
            Manage AI model configurations for benchmark runs
          </Text>
        </div>
        <Flex gap="md" align="center">
          <Select
            placeholder="Filter by job type"
            items={jobTypeFilterOptions}
            selectedKey={filterJobType}
            onSelectionChange={(key) => setFilterJobType(String(key))}
            className="min-w-[180px]"
          />
          <Button onPress={handleNewConfig}>
            <Plus size={16} />
            New Config
          </Button>
        </Flex>
      </Flex>

      {/* Config form */}
      {showForm && (
        <Card padding="lg">
          <CardHeader>
            <Heading level={3}>
              {editingConfig ? 'Edit Configuration' : 'New Configuration'}
            </Heading>
          </CardHeader>
          <CardContent>
            <Flex direction="column" gap="lg">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Box>
                  <Text size="sm" className="text-muted-foreground mb-1 block">
                    Name *
                  </Text>
                  <input
                    value={formData.name}
                    onChange={(e) => setFormData((f) => ({ ...f, name: e.target.value }))}
                    className="w-full border border-border rounded-md px-3 py-2 text-sm bg-background"
                    placeholder="e.g., Production GPT-4"
                  />
                </Box>
                <Box>
                  <Text size="sm" className="text-muted-foreground mb-1 block">
                    Job Type {editingConfig ? '(immutable)' : '*'}
                  </Text>
                  <Select
                    placeholder="Select job type"
                    items={JOB_TYPE_OPTIONS}
                    selectedKey={formData.jobType}
                    onSelectionChange={(key) =>
                      setFormData((f) => ({ ...f, jobType: key as BenchmarkJobType }))
                    }
                    isDisabled={Boolean(editingConfig)}
                    className="w-full"
                  />
                </Box>
              </div>

              <Box>
                <Text size="sm" className="text-muted-foreground mb-1 block">
                  Description
                </Text>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData((f) => ({ ...f, description: e.target.value }))}
                  className="w-full border border-border rounded-md px-3 py-2 text-sm bg-background min-h-[60px]"
                  placeholder="Optional description..."
                />
              </Box>

              <Divider />

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Box>
                  <Text size="sm" className="text-muted-foreground mb-1 block">
                    Provider
                  </Text>
                  <Select
                    placeholder="Select provider"
                    items={providerOptions}
                    selectedKey={formData.provider}
                    onSelectionChange={(key) => {
                      const provider = String(key);
                      // Drop a model that the newly chosen provider can't serve.
                      const allowed = modelOptionsForProvider(catalog, provider || null);
                      setFormData((f) => ({
                        ...f,
                        provider,
                        model: f.model && allowed.includes(f.model) ? f.model : '',
                      }));
                    }}
                    className="w-full"
                  />
                </Box>
                <Box>
                  <Text size="sm" className="text-muted-foreground mb-1 block">
                    Model
                  </Text>
                  <Select
                    placeholder={catalogQuery.isLoading ? 'Loading…' : 'Select model'}
                    items={modelOptions}
                    selectedKey={formData.model}
                    isDisabled={catalogQuery.isLoading}
                    onSelectionChange={(key) => setFormData((f) => ({ ...f, model: String(key) }))}
                    className="w-full"
                  />
                </Box>
                <Box>
                  <Text size="sm" className="text-muted-foreground mb-1 block">
                    Prompt Version
                  </Text>
                  <input
                    value={formData.promptVersion}
                    onChange={(e) => setFormData((f) => ({ ...f, promptVersion: e.target.value }))}
                    className="w-full border border-border rounded-md px-3 py-2 text-sm bg-background"
                    placeholder="e.g., v2.1"
                  />
                </Box>
              </div>

              <Box>
                <Text size="sm" className="text-muted-foreground mb-1 block">
                  Job Config (JSON)
                </Text>
                <textarea
                  value={formData.jobConfig}
                  onChange={(e) => setFormData((f) => ({ ...f, jobConfig: e.target.value }))}
                  className="w-full border border-border rounded-md px-3 py-2 text-sm font-mono bg-background min-h-[100px]"
                  placeholder='{"maxPages": 50, "timeoutSeconds": 300}'
                  spellCheck={false}
                />
                <Text size="xs" className="text-muted-foreground mt-1">
                  Job-specific settings like maxPages, timeoutSeconds, maxSteps, etc.
                </Text>
              </Box>

              <Flex align="center" gap="sm">
                <input
                  type="checkbox"
                  id="isDefault"
                  checked={formData.isDefault}
                  onChange={(e) => setFormData((f) => ({ ...f, isDefault: e.target.checked }))}
                  className="rounded"
                />
                <label htmlFor="isDefault">
                  <Text size="sm">Set as default for this job type</Text>
                </label>
              </Flex>

              {formError && <Text className="text-destructive">{formError}</Text>}

              <Flex gap="md" justify="end">
                <Button variant="outline" onPress={handleCloseForm}>
                  Cancel
                </Button>
                <Button
                  onPress={handleSubmit}
                  isDisabled={createMutation.isPending || updateMutation.isPending}
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? 'Saving...'
                    : editingConfig
                      ? 'Update'
                      : 'Create'}
                </Button>
              </Flex>
            </Flex>
          </CardContent>
        </Card>
      )}

      {/* Configs table */}
      <Card padding="lg">
        <CardHeader>
          <Heading level={3}>Configurations</Heading>
          <Text size="sm" className="text-muted-foreground mt-1">
            {configsQuery.data?.configs.length ?? 0} config(s) found
          </Text>
        </CardHeader>
        <CardContent>
          {configsQuery.isLoading ? (
            <Flex align="center" justify="center" className="py-10">
              <LoadingOrbit size="lg" />
            </Flex>
          ) : configsQuery.isError ? (
            <Text className="text-destructive">
              {configsQuery.error instanceof Error
                ? configsQuery.error.message
                : 'Failed to load configs'}
            </Text>
          ) : configsQuery.data?.configs.length ? (
            <DataTable columns={columns} data={configsQuery.data.configs} />
          ) : (
            <Text className="text-muted-foreground">
              No configurations found. Create one to get started.
            </Text>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
